import React, {useEffect, useState} from "react";
import {Box, Divider} from '@mui/material';

import "../App.css"
import {PageHeader} from "./PageHeader";
import {DocPanel} from "./DocPanel";
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {useCrewCtx} from "../app/CrewCtx";
import { i18n } from "../ctx/I18n";
import {accessCtrl, canEditProject, canManageDoc, canManageTask} from "../ctx/AccessCtrl"
import {CxDataGrid} from "../cmx/CxData";
import {CxTextField, CxComboxSelection, CxCheckBox} from "../cmx/CxForm";
import {CxGrid} from "../cmx/CxGrid";
import {CxListFeedback} from "../cmx/CxFeedback";
import {Task} from "../domain/Task";
import {Project} from "../domain/Project"
import {runningCall, successCall} from "../gateway/CallService";
import {projectTaskList} from "../gateway/ProjectService"
import {taskClearedUpdate} from "../gateway/TaskService";
import {DateUtil} from "../lib/DateUtil";
import {NumberUtil} from "../lib/NumberUtil";

// --------------------------------------------------------------------------------------------------------------------
// renderHeader
// --------------------------------------------------------------------------------------------------------------------

const RenderHeader = (projectData) => {
    const { handleAction } = useAppCtx();

    const handleLocalActionChange = (value) => {
        if (value === "projectUpdate") {
            handleAction("projectUpdate", projectData)
        }
    };

    const title = `Projekt ${projectData.project_ref}`;

    const localActionItems= [
        { kind: "icon",    label: "Bearbeiten", iconName: "edit",   actionName: "projectUpdate",
            disabled: !canEditProject(projectData.foreman_crew_id, projectData.deputy_foreman_crew_id) }
    ]

    const actionItems = [
        ...(accessCtrl.canViewAvorProjectList ? [
            { kind: "tooltip", label: "Zurück zu Vorausmass", iconName: "avor", actionName: "projectAvor" }
        ] : []),
        { kind: "tooltip", label: "Zurück zu Projekte", iconName: "project", actionName: "project", disabled: !accessCtrl.canCtrlTaskList },
    ];

    return (
        <PageHeader
            title={title}
            localActionItems={localActionItems}
            localActionOnChangeHandler={handleLocalActionChange}
            actionItems={actionItems} />
    );
}

const RenderTasksHeader = (projectData) => {
    const { handleAction, setPageData, getPageState, setPageState } = useAppCtx();

    const title = "Arbeitsvorgänge";

    // actions handling
    const handleLocalActionChange = (value) => {
        if (value === "taskCreate") {
            setPageData('ProjectDetail', 'project', projectData)
            handleAction("taskCreate", projectData)
        }
    };

    const localActionItems= [
        { kind: "icon",    label: "Erstellen",   iconName: "create", actionName: "taskCreate",
            disabled: !canManageTask(projectData.foreman_crew_id, projectData.deputy_foreman_crew_id) }
    ]

    const actionItems = [
        { kind: "tooltip", label: "Zurück zu Planung",     iconName: "plan",    actionName: "task" }
    ];

    // task view handling
    const taskView = getPageState('ProjectDetail', 'taskView');

    const handleTaskViewChange = (value) => {
        setPageState('ProjectDetail', 'taskView', value);
    };

    const taskViewItems = [
        { label: "alle",          value: "allTaskView" },
        { label: "nur aktuelle",  value: "actualTaskView" },
    ];

    return (
        <PageHeader
            title={title}
            isSubtitle={true}
            viewItems={taskViewItems}
            viewInitialValue={taskView}
            viewOnChangeHandler={handleTaskViewChange}
            localActionItems={localActionItems}
            localActionOnChangeHandler={handleLocalActionChange}
            actionItems={actionItems}
        />
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderProject
// --------------------------------------------------------------------------------------------------------------------

const RenderProject = (currentData) => {
    const {crew, lazyloadCrews} = useCrewCtx();         // load crew for RenderProject and RenderTasks
    const {lazyloadCodes} = useCodeCtx();         // load code for RenderTasks already here

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []);

    const formValues = { ...currentData };          // make a copy, to not lose original data
    const formErrors = Project.getFormErrors();

    formValues.initial_date = DateUtil.convertIsoStringToString(formValues.initial_date);
    formValues.foreman_crew_id = formValues.foreman_crew_id === null ? -1 : formValues.foreman_crew_id;
    formValues.deputy_foreman_crew_id = formValues.deputy_foreman_crew_id === null ? -1 : formValues.deputy_foreman_crew_id;
    formValues.avor_amount = NumberUtil.formatCHF(formValues.avor_amount);
    formValues.avor_created_at = DateUtil.convertIsoStringToString(formValues.avor_created_at);

    // inputs
    const formTextField = (field, label) => {
        return <CxTextField field={field} label={label} readOnly={true}
                            formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]}
                                  items={items} formErrors={formErrors} readOnly={true} />
    }

    const formCheckBox = (field, label) => {
        return <CxCheckBox field={field} label={label} readOnly={true}
                           formValues={formValues} formErrors={formErrors} />
    }

    // return content
    return (
        <>
            <CxGrid size="lg">{ formTextField("project_ref", "Projekt Nr *") }</CxGrid>
            <CxGrid size="lg">{ formTextField("project_desc", "Projekt Beschreibung") }</CxGrid>

            <CxGrid size="lg">{ formTextField("architect", "Auftraggeber") }</CxGrid>
            <CxGrid size="lg">{ formTextField("initial_date", "Projektstart *")}</CxGrid>

            <CxGrid size="lg">{ formTextField("address", "Adresse") }</CxGrid>
            <CxGrid size="sm">{ formTextField("zip", "PLZ") }</CxGrid>
            <CxGrid size="sm">{ formTextField("city", "Ort") }</CxGrid>

            <CxGrid size="lg">{ formTextField("sm_name", "Bauleiter/Ansprechperson vor Ort") }</CxGrid>
            <CxGrid size="sm">{ formTextField("sm_phone", "Bauleiter Telefon") }</CxGrid>
            <CxGrid size="sm">{ formTextField("sm_email", "Bauleiter E-Mail") }</CxGrid>

            <CxGrid size="sm">{ formTextField("avor_amount", "Auftragssumme") }</CxGrid>
            <CxGrid size="sm">{ formCombox("avor_state", "Vorausmass", Project.getAvorStateItems()) }</CxGrid>
            <CxGrid size="sm">{ formCombox("avor_created_crew_id", "Vorausmass erstellt von", crew.getAvorSelection()) }</CxGrid>
            <CxGrid size="sm">{ formTextField("avor_created_at", "Vorausmass erstellt am") }</CxGrid>

            <CxGrid size="sm">{ formCombox("foreman_crew_id", "Bauführer", crew.getForemanSelection()) }</CxGrid>
            <CxGrid size="sm">{ formCombox("deputy_foreman_crew_id", "Bauführer Stellvertreter", crew.getForemanSelection()) }</CxGrid>
            <CxGrid size="sm" valign="middle">{ formCheckBox("closed", "Abgeschlossen") }</CxGrid>
            <CxGrid size="sm" />

            <CxGrid size="xl">{ formTextField("prep_info", i18n.prepInfo, 2048) }</CxGrid>

            <CxGrid size="xs">{ formCombox("prep_1", i18n.prep1, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_2", i18n.prep2, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_3", i18n.prep3, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_4", i18n.prep4, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_5", i18n.prep5, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_6", i18n.prep6, Project.getPrepStateItems()) }</CxGrid>
        </>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderTasks
// --------------------------------------------------------------------------------------------------------------------

const RenderTasks = (projectData) => {
    const { handleAction, setPageData, getPageState } = useAppCtx();
    const { code } = useCodeCtx();
    const { crew } = useCrewCtx();

    //
    // select tasks
    //
    const [call, setCall] = useState(runningCall());

    useEffect(() => {
        setCall(runningCall())
        projectTaskList(projectData.id).then(responseCall => {
            if (responseCall.isSuccess) {
                const mappedRows = Task.mapListColumns(responseCall.result, code, crew, true);
                setCall(successCall(mappedRows));

            } else {
                setCall(responseCall);
            }
        });

    }, [projectData.id, code, crew]);


    //
    // filter tasks
    //
    const taskView = getPageState('ProjectDetail', 'taskView');     // 'allTaskView' or 'actualTaskView'

    let rows = [];
    if (call.isSuccess) {
        const todayIso = DateUtil.getCurrentDateAsIsoString();
        rows = call.result;
        if (taskView === 'actualTaskView') {
            rows = rows.filter(task => task.end_date >= todayIso);
        }
    }

    //
    // show tasks
    //
    const handleRowDoubleClick = (params) => {
        setPageData('ProjectDetail', 'project', projectData)
        handleAction("taskShow", params.row)
    };

    const handleEditClick = (params) => {
        setPageData('ProjectDetail', 'project', projectData)
        handleAction("taskUpdate", params.row)
    }

    const handleCloneClick = (params) => {
        setPageData('ProjectDetail', 'project', projectData)
        handleAction("taskClone", params.row)
    }

    const handleToggleClearedClick = async (row) => {
        const newClearedValue = row.cleared === 0 ? 1 : 0;

        const responseCall = await taskClearedUpdate(row.id, newClearedValue);
        if (responseCall.isSuccess) {
            // replace updated row in state
            const updatedRows = call.result.map(task =>
                task.id === row.id ? { ...task, cleared: newClearedValue } : task
            );
            setCall(successCall(updatedRows));  // triggers re-render
        }
    };

    const enableOnEdit = canManageTask(projectData.foreman_crew_id, projectData.deputy_foreman_crew_id);

    return (
        <>
            <CxListFeedback call={call} />

            { call.isSuccess &&
                <CxGrid size="xl">
                    <CxDataGrid
                        rows={rows}
                        columns={Task.getProjectDetailColumns(false)}
                        onRowDoubleClick={handleRowDoubleClick}
                        onViewAction={handleRowDoubleClick}
                        onEditAction={enableOnEdit ? handleEditClick : undefined}
                        onCloneAction={enableOnEdit ? handleCloneClick: undefined}
                        onToggleClearedAction={handleToggleClearedClick}
                    />
                </CxGrid>
            }
        </>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// RenderDetail
// --------------------------------------------------------------------------------------------------------------------

const RenderDetail = (projectData) => {

    const canManage = canManageDoc(projectData.foreman_crew_id, projectData.deputy_foreman_crew_id);

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { RenderHeader(projectData) }
                { RenderProject(projectData) }
            </CxGrid>

            <Divider sx={{ my: 4 }} />

            <CxGrid container spacing={2}>
                { RenderTasksHeader(projectData) }
                { RenderTasks(projectData) }
            </CxGrid>

            <Divider sx={{ my: 4 }} />

            <CxGrid container spacing={2}>
                <DocPanel
                    projectId={projectData.id}
                    docCount={projectData.doc_count}
                    canManage={canManage}
                />
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// ProjectDetail
// --------------------------------------------------------------------------------------------------------------------

export function ProjectDetail() {
    const { currentData, getPageData } = useAppCtx();   // currentData contains project data from project search
    let projectData = null                         // getPageData delivers project data from project list

    if (currentData) {
        projectData = currentData;
    } else {
        projectData = getPageData('ProjectDetail', 'project')
    }

    return RenderDetail(projectData);
}

export function ProjectDetailFromTask() {
    const { currentData } = useAppCtx();                // currentData contains task data from task search
    return RenderDetail(Task.extractProjectData(currentData));
}

// --------------------------------------------------------------------------------------------------------------------
// The end.